import axios from "axios";
import { API_BASE_PATH } from "../../environment";
import { toast } from "react-toastify";

const CLIENT_BASE_URL = API_BASE_PATH;

export interface ClientAPIResponse {
  client_id: string;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone: string;
  company: string;
  photo: string;
  client_gst_no: string;
  client_pan_card: string;
  client_join_date: string;
  client_type: string;
  is_active: boolean;
  Status: string;
}

export interface ClientListFormatted {
  id: string;
  key: string;
  ClientID: string;
  ClientName: string;
  Work: string;
  CompanyName: string;
  Email: string;
  Phone: string;
  Image: string;
  Status: string;
}

// ✅ Error extractor
const extractErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail;
  let details = error?.response?.data?.details;
  const message = error?.response?.data?.message;

  if (typeof details === "string") {
    details = details.replace(/^\d+\s*:\s*/, "");
  }

  return detail || details || message;
};

export const fetchClientListDetails = async (): Promise<
  ClientListFormatted[]
> => {
  try {
    const response = await axios.get<ClientAPIResponse[]>(
      `${CLIENT_BASE_URL}/client/client/all`,
      {
        headers: {
          accept: "application/json",
        },
      }
    );
    return response.data.map((client, index) => ({
      key: (index + 1).toString(),
      id: client.client_id,
      ClientID: client.client_id || `Cli-${index + 1}`,
      ClientName: `${client.first_name} ${client.last_name}`,
      Work: client.client_type || "N/A",
      CompanyName: client.company,
      Email: client.email,
      Phone: client.phone,
      Image: client.photo || "default.jpg",
      Status: client.Status === "active" ? "Active" : "Inactive",
    }));
  } catch (error) {
    console.error("Error fetching client list:", extractErrorMessage(error));
    return [];
  }
};

export interface WidgetCountsResponse {
  counts: {
    clients: {
      total: number;
    };
    active_client_status: {
      active_client: number;
      total: number;
    };
    inactive_client_status: {
      inactive_client: number;
      total: number;
    };
    new_client: {
      total_new_client: number;
    };
  };
}

export const fetchClientWidgetCounts =
  async (): Promise<WidgetCountsResponse | null> => {
    try {
      const response = await axios.get<WidgetCountsResponse>(
        `${CLIENT_BASE_URL}/client/client_dash_widegets`,
        {
          headers: {
            accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching widget counts:", extractErrorMessage(error));
      return null;
    }
  };

export interface ClientFormPayload {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone: string;
  company: string;
  password: string;
  client_pan_card: string;
  client_gst_no: string;
  client_join_date: string;
  client_type: string;
  Status: string;
  photo: File; // File object from <input type="file">
}

export interface ClientPayload {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone: string;
  company: string;
  password: string;
  client_pan_card: string;
  client_gst_no: string;
  client_join_date: string; // format: 'YYYY-MM-DD'
  client_type: string;
  Status: string;
  photo: File | null;
}

export const postClientDetails = async (data: ClientPayload): Promise<void> => {
  const formData = new FormData();
  formData.append("first_name", data.first_name);
  formData.append("last_name", data.last_name);
  formData.append("username", data.username);
  formData.append("email", data.email);
  formData.append("phone", data.phone);
  formData.append("company", data.company);
  formData.append("password", data.password);
  formData.append("client_pan_card", data.client_pan_card);
  formData.append("client_gst_no", data.client_gst_no);
  formData.append("client_join_date", data.client_join_date);
  formData.append("client_type", data.client_type);
  formData.append("Status", data.Status);

  if (data.photo) {
    formData.append("photo", data.photo);
  }

  try {
    await axios.post(`${CLIENT_BASE_URL}/client`, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Failed to post client:", message);
    toast.error(message);
    throw error;
  }
};

export const deleteClientById = async (clientId: string): Promise<void> => {
  try {
    await axios.delete(`${CLIENT_BASE_URL}/client/client/delete/${clientId}`, {
      headers: {
        accept: "application/json",
      },
    });
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error(`Failed to delete client ${clientId}:`, message);
    toast.error(message);
    throw error;
  }
};

export const fetchClientById = async (
  clientId: string
): Promise<ClientAPIResponse | null> => {
  try {
    const response = await axios.get<ClientAPIResponse>(
      `${CLIENT_BASE_URL}/client/client/${clientId}`,
      {
        headers: {
          accept: "application/json",
        },
      }
    );

    const raw = response.data;

    const transformed: ClientAPIResponse = {
      client_id: raw.client_id,
      first_name: raw.first_name?.trim() ?? "",
      last_name: raw.last_name?.trim() ?? "",
      username: raw.username ?? "",
      email: raw.email?.toLowerCase() ?? "",
      phone: raw.phone ?? "",
      company: raw.company ?? "",
      photo: raw.photo ?? "default.jpg",
      client_gst_no: raw.client_gst_no ?? "",
      client_pan_card: raw.client_pan_card ?? "",
      client_join_date: raw.client_join_date ?? "",
      client_type: raw.client_type ?? "N/A",
      is_active: raw.is_active ?? raw.Status?.toLowerCase() === "active",
      Status: raw.Status ?? "inactive",
    };
    return transformed;
  } catch (error) {
    console.error(`Failed to fetch client ${clientId}:`, extractErrorMessage(error));
    return null;
  }
};

export interface ClientUpdatePayload {
  client_id: string;
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  phone: string;
  company: string;
  photo: File | null;
  client_gst_no: string;
  client_pan_card: string;
  client_join_date: string; // 'YYYY-MM-DD'
  client_type: string;
  Status: string;
  password?: string;
  is_active?: boolean | string;
}

export const updateClientDetails = async (
  data: ClientUpdatePayload
): Promise<void> => {
  const formData = new FormData();

  // ✅ Append required fields
  formData.append("first_name", data.first_name);
  formData.append("last_name", data.last_name);
  formData.append("username", data.username);
  formData.append("email", data.email);
  formData.append("phone", data.phone);
  formData.append("company", data.company);
  formData.append("client_pan_card", data.client_pan_card);
  formData.append("client_gst_no", data.client_gst_no);
  formData.append("client_join_date", data.client_join_date);
  formData.append("client_type", data.client_type);
  formData.append("Status", data.Status);
  if (data.password) {
    formData.append("password", data.password);
  }

  if (data.is_active !== undefined) {
    formData.append(
      "is_active",
      data.is_active === true || data.is_active === "true" ? "true" : "false"
    );
  }

  if (data.photo) {
    formData.append("photo", data.photo);
  } else {
    // Empty blob to satisfy multipart expectations
    formData.append("photo", new Blob(), "empty.jpg");
  }

  try {
    await axios.put(`${CLIENT_BASE_URL}/client/${data.client_id}`, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
    });
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Failed to update client:", message);
    toast.error(message);
    throw error;
  }
};

// =============================
// Export Selected Clients as PDF
// =============================
export const exportSelectedClientsPdf = async (clientIds: string[]) => {
  try {
    if (clientIds.length === 0) {
      toast.warning("No client IDs selected to export as PDF.");
      return;
    }

    for (const id of clientIds) {
      const response = await axios.get(
        `${CLIENT_BASE_URL}/clients/export/pdf/${id}`,
        {
          headers: { accept: "application/json" },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Client_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

    toast.success("Selected client PDFs downloaded.");
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Export PDF failed:", message);
    toast.error(message);
  }
};

// =============================
// Export Selected Clients as Excel
// =============================
export const exportSelectedClientsExcel = async (clientIds: string[]) => {
  try {
    if (clientIds.length === 0) {
      toast.warning("No client IDs selected to export as Excel.");
      return;
    }

    for (const id of clientIds) {
      const response = await axios.get(
        `${CLIENT_BASE_URL}/clients/export/excel/${id}`,
        {
          headers: { accept: "application/json" },
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Client_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

    toast.success("Selected client Excels downloaded.");
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Export Excel failed:", message);
    toast.error(message);
  }
};

// =============================
// Export All Clients as PDF
// =============================
export const exportAllClientsPdf = async () => {
  try {
    const response = await axios.get(
      `${CLIENT_BASE_URL}/clients/clients/export/pdf`,
      {
        headers: { accept: "application/json" },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], { type: "application/pdf" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "All_Clients.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success("All clients PDF downloaded.");
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Failed to export all clients as PDF:", message);
    toast.error(message);
  }
};

// =============================
// Export All Clients as Excel
// =============================
export const exportAllClientsExcel = async () => {
  try {
    const response = await axios.get(
      `${CLIENT_BASE_URL}/clients/clients/export/excel`,
      {
        headers: { accept: "application/json" },
        responseType: "blob",
      }
    );

    const blob = new Blob([response.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "All_Clients.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    toast.success("All clients Excel downloaded.");
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Failed to export all clients as Excel:", message);
    toast.error(message);
  }
};

/**
 * Update existing clients from an Excel/CSV file
 * Endpoint: PUT /clients/excel-update
 */
export const updateClientsFromExcel = async (file: File): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    await axios.put(`${CLIENT_BASE_URL}/clients/excel-update`, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
    });

    toast.success("Client records updated successfully from Excel.");
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Excel update failed:", message);
    toast.error(message);
    throw error;
  }
};

/**
 * Import new clients from an Excel/CSV file
 * Endpoint: POST /clients/import-excel/
 */
export const importClientsFromExcel = async (file: File): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    await axios.post(`${CLIENT_BASE_URL}/clients/import-excel`, formData, {
      headers: {
        accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
    });

    toast.success("Clients imported successfully from Excel.");
  } catch (error) {
    const message = extractErrorMessage(error);
    console.error("Excel import failed:", message);
    toast.error(message);
    throw error;
  }
};
